import React, { useState } from 'react';

import Modal from '@rover/components/src/OverflowTextModal/Modal';
import AutomaticRecordContent from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/components/ReviewsSection/AutomaticRecordContent/AutomaticRecordContent';
import DeleteForm from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/components/ReviewsSection/DeleteForm/DeleteForm';
import FullReviewsList from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/components/ReviewsSection/FullReviewsList';
import ReplyForm from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/components/ReviewsSection/ReplyForm';
import AutoHeight from '@rover/react-lib/src/pages/sitter-profile/SitterProfilePage/utils/AutoHeight/AutoHeight';
import { ApiPersonDetailReviewsListPetType as PetTypeReview } from '@rover/rsdk/src/apiClient/latest/model/apiPersonDetailReviewsListPetType';
import { AutomaticRecordTypeEnum } from '@rover/rsdk/src/apiClient/latest/model/automaticRecordTypeEnum';
import { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';

type Props = {
  opk: string;
  highlightedReviewOpk?: string | null;
  modalOpen: boolean;
  closeModal: () => void;
  avatarUrl: string;
  scrollIndex?: number;
  petTypeFilter?: (typeof PetTypeReview)[keyof typeof PetTypeReview] | null;
  onFilterChange?: (filter: (typeof PetTypeReview)[keyof typeof PetTypeReview] | null) => void;
  title?: string;
  seeMoreButtonClick?: boolean;
  setSeeMoreButtonClick: (click: boolean) => void;
  children?: JSX.Element | null;
  showRating?: boolean;
};

const ReviewsModal = ({
  modalOpen,
  closeModal,
  opk,
  highlightedReviewOpk = null,
  avatarUrl,
  scrollIndex = 0,
  petTypeFilter,
  onFilterChange,
  title,
  setSeeMoreButtonClick,
  seeMoreButtonClick = false,
  children = null,
  showRating = false,
}: Props): JSX.Element => {
  const [selectedReply, setSelectedReply] = useState<ReviewDispatch | undefined>();
  const [replyToDelete, setReplyToDelete] = useState<ReviewDispatch | undefined>();
  const [automaticRecordModal, setAutomaticRecordModal] = useState<boolean>(false);

  const fullList = !selectedReply && !replyToDelete && !automaticRecordModal;

  const toggleAutomaticRecordModal = (): void => setAutomaticRecordModal(!automaticRecordModal);

  const handleReplyClick = (review: ReviewDispatch): void => {
    setSelectedReply(review);
  };

  const handleFinishReply = (): void => {
    setSelectedReply(undefined);
    setReplyToDelete(undefined);
  };

  const handleStartDelete = (review: ReviewDispatch): void => {
    setReplyToDelete(review);
    setSelectedReply(undefined);
  };

  const handleCloseModal = (): void => {
    if (automaticRecordModal) {
      setAutomaticRecordModal(false);
      return;
    }
    setSelectedReply(undefined);
    setReplyToDelete(undefined);
    setSeeMoreButtonClick(false);
    closeModal();
  };

  return (
    <Modal
      headerText={!automaticRecordModal ? title : undefined}
      isOpen={modalOpen}
      onClose={handleCloseModal}
      hasCloseButton={fullList}
      sx={{
        paddingRight: '0x',
        marginX: '-4x',
      }}
    >
      <AutoHeight>
        {automaticRecordModal && (
          <AutomaticRecordContent
            onClose={toggleAutomaticRecordModal}
            onBack={toggleAutomaticRecordModal}
          />
        )}
        {replyToDelete && (
          <DeleteForm
            onBack={handleFinishReply}
            onClose={handleCloseModal}
            review={replyToDelete}
            sitterAvatar={avatarUrl}
            sitterOpk={opk}
          />
        )}
        {selectedReply && (
          <ReplyForm
            review={selectedReply}
            onBack={handleFinishReply}
            onClose={handleCloseModal}
            isAutomaticRecord={selectedReply.type in AutomaticRecordTypeEnum}
            sitterOpk={opk}
          />
        )}
        {fullList && (
          <FullReviewsList
            opk={opk}
            highlightedReviewOpk={highlightedReviewOpk}
            scrollIndex={scrollIndex}
            sitterAvatar={avatarUrl}
            onReplyClick={handleReplyClick}
            onDelete={handleStartDelete}
            onLearnMore={toggleAutomaticRecordModal}
            petTypeFilter={petTypeFilter}
            onFilterChange={onFilterChange}
            seeMoreButtonClick={seeMoreButtonClick}
            showRating={showRating}
          >
            {children}
          </FullReviewsList>
        )}
      </AutoHeight>
    </Modal>
  );
};

export default ReviewsModal;
